import { useTranslation } from 'react-i18next';

import type { GameType, ServerGoalProgress } from '@ottd/api';

import { companyColorHex } from '@/components/prototype/president-face';
import { ProgressBar } from '@/components/prototype/progress-bar';
import { formatGoalFigure } from '@/lib/format';
import { cn } from '@/lib/utils';
import { PlayerLink } from '@/pages/shared';

interface GoalProgressMeterProps {
  progress: ServerGoalProgress;
  type: GameType | null;
  /** Bar and percentage only, for the dense server list card. */
  compact?: boolean;
  className?: string;
}

/** Whole percent, rounded down: 99.6% must not read as a finished "100%". */
function wholePercent(progress: number): number {
  return Math.max(0, Math.floor(progress));
}

/**
 * How close a running game is to ending: unless compact, who leads - in their
 * company colour, with the figure they lead with (town population or company
 * value) - then their bar towards the goal and the percentage underneath. A
 * leader with no resolved player reads as their company instead.
 */
export function GoalProgressMeter({
  progress,
  type,
  compact = false,
  className,
}: GoalProgressMeterProps) {
  const { t } = useTranslation('server');
  const { t: tc } = useTranslation('common');
  const percent = wholePercent(progress.progress);
  const color = companyColorHex(progress.color);
  const figure = formatGoalFigure(progress.figure, type);

  return (
    <div className={cn('flex min-w-0 flex-col gap-1', className)}>
      {compact ? null : (
        <p className="text-dim flex min-w-0 items-center gap-1.5 text-xs">
          <span
            className="size-2.5 shrink-0 border border-black"
            style={{ background: color }}
            data-testid="leader-company-chip"
            aria-hidden
          />
          <span className="truncate">
            {progress.player ? (
              <PlayerLink name={progress.player} />
            ) : (
              <span style={{ color }}>{progress.company}</span>
            )}{' '}
            {t('progress.leads')}
            <span className="text-faint"> · </span>
            <span className="text-strong font-mono">{figure}</span>
          </span>
        </p>
      )}
      <div className="flex items-center gap-2">
        <ProgressBar
          value={progress.progress}
          height={compact ? 6 : 8}
          label={t('progress.label', { company: progress.company })}
          className="min-w-0 flex-1"
        />
        <span className="text-green shrink-0 font-mono text-[11px]">
          {compact ? `${percent}%` : tc('units.percentOfGoal', { percent })}
        </span>
      </div>
    </div>
  );
}
